import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import throttle from 'lodash.throttle'

import { RescheduleReservation, Reservation } from '~/views/Reservation'

import CallControllers from '~/components/CallControllers'
import CallHeader from '~/components/CallHeader'
import CallHeaderAlert from '~/components/CallHeaderAlert'
import ChatContainer from '~/components/ChatContainer'
import CustomIcons, { iconList } from '~/components/CustomIcons'
import FeedbackCallMessageBox from '~/components/FeedbackCallMessageBox'
import GuestEndCallModal from '~/components/GuestEndCallModal'
import NetworkSignal from '~/components/NetworkSignal'
import PatientDetails from '~/components/PatientDetails'
import ReportErrorButton from '~/components/ReportErrorButton'

import { errorTypes } from '../ErrorView/Errors'

import { DevicesContext } from '~/contexts'
import { fetchApi } from '~/handlers'
import { fetchNocoDB } from '~/handlers/apiHandler'
import { useGetReservation } from '~/hooks'
import { JitsiMeet } from '~/modules/Jitsi'
import { externalRoutes } from '~/settings'
import { colors } from '~/utils/stylesConstants'
import { createTaskQueue } from '~/utils/TaskQueue'

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

const constraints = {
  video: {
    facingMode: 'user',
    width: isMobile
      ? { ideal: 640, max: 640, min: 320 }
      : { ideal: 1280, max: 1920, min: 640 },
    height: isMobile
      ? { ideal: 480, max: 480, min: 240 }
      : { ideal: 720, max: 1080, min: 480 },
    frameRate: { ideal: 30, max: 30, min: 15 },
    aspectRatio: 16 / 9
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  },
}

const RELOAD_ERRORS = [
  'conference.incompatibleServerVersions',
  'conference.restarted',
]

const LEAVE_BEFORE_DISCONNECT_ERRORS = [
  'conference.iceFailed',
  'conference.videobridgeNotAvailable',
  'conference.offerAnswerFailed',
  'conference.focusLeft',
]

const NOTIFY_ONLY_ERRORS = [
  'conference.focusDisconnected',
  'connection.passwordRequired',
]

const ERROR_MESSAGES = {
  'conference.connectionError': 'Erro de conexão. Verificando rede...',
  'conference.setupFailed': 'Falha ao configurar conferência.',
  'conference.reservationError': 'Erro de reserva de sala.',
  'conference.gracefulShutdown': 'Servidor em manutenção.',
  'conference.iceFailed': 'Falha na conexão de rede (ICE).',
  'conference.focusDisconnected': 'Servidor temporariamente indisponível.',
  'conference.focusLeft': 'Servidor desconectado.',
  'connection.connectionDropped': 'Conexão perdida. Verificando rede...',
  'connection.connectionFailed': 'Falha ao conectar.',
  'connection.passwordRequired': 'Sala protegida por senha.',
  'connection.serverError': 'Erro no servidor.',
}

const getDefaultDeviceId = async (kind) => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const defaultDevice = devices.find(device =>
      device.kind === kind && device.deviceId === 'default'
    )

    if (defaultDevice) {
      const realDevice = devices.find(device =>
        device.kind === kind &&
        device.deviceId !== 'default' &&
        device.groupId === defaultDevice.groupId
      )

      if (realDevice) {
        console.log('[JitsiRoom1] Device \'default\' mapeado para:', realDevice.deviceId)
        return realDevice.deviceId
      }
    }

    const firstDevice = devices.find(device => device.kind === kind && device.deviceId !== 'default')
    if (firstDevice) {
      return firstDevice.deviceId
    }

    return 'default'
  } catch (error) {
    console.error('[JitsiRoom1] Erro ao obter deviceId padrão:', error)
    return 'default'
  }
}

const getQualityScoreFromPercentage = (connectionQuality = 0) => {
  if (connectionQuality > 80) return 5 // Excellent
  if (connectionQuality > 60) return 4 // Good
  if (connectionQuality > 40) return 3 // Average
  if (connectionQuality > 20) return 2 // Below
  if (connectionQuality > 0) return 1 // Bad
  return 0 // Network broken or 0%
}

const JitsiRoom = ({ meeting, onOpenSettingsModal }) => {
  const localVideoRef = useRef(null)
  const remoteAudioRef = useRef(null)
  const remoteVideoRef = useRef(null)
  const hideControlsTimerRef = useRef(null)

  const connectionRef = useRef(null)
  const conferenceRef = useRef(null)
  const localTracksRef = useRef({ audio: null, video: null })
  const remoteTracksRef = useRef(new Map())
  const transcriberIdsRef = useRef(new Set())
  const participantNamesRef = useRef(new Map())

  const replaceAudioTrackQueueRef = useRef(null)
  const replaceVideoTrackQueueRef = useRef(null)

  if (!replaceAudioTrackQueueRef.current) {
    replaceAudioTrackQueueRef.current = createTaskQueue()
  }
  if (!replaceVideoTrackQueueRef.current) {
    replaceVideoTrackQueueRef.current = createTaskQueue()
  }

  const isDefaultMicSelectedRef = useRef(false)
  const isDefaultCameraSelectedRef = useRef(false)

  const history = useHistory()
  const { schedule: { guests }, user } = meeting
  const professional = guests.find(u => ['TVTVPSIC', 'TVTVPSIQ'].includes(u.profile_code))
  const { reservation, refetch: refetchReservation } = useGetReservation({
    professionalUsername: professional?.username,
    patientUsername: meeting.guest.username,
  })

  const {
    activeMicrophoneDevice,
    activeVideoDevice,
  } = useContext(DevicesContext)

  const [audioEnabled, setAudioEnabled] = useState(true)
  const [videoEnabled, setVideoEnabled] = useState(true)

  const [isRemoteAudioMuted, setIsRemoteAudioMuted] = useState(false)
  const [isRemoteVideoMuted, setIsRemoteVideoMuted] = useState(false)
  const [localNetwork, setLocalNetwork] = useState(null)
  const [participantNetwork, setParticipantNetwork] = useState(null)

  const [statusMessage, setStatusMessage] = useState('Aguardando participante entrar...')
  const [participantLeft, setParticipantLeft] = useState(false)
  const [chatMessage, setChatMessage] = useState(null)
  const [isChatOpen, setIsChatOpen] = useState(false)

  const [reservationOpen, setReservationOpen] = useState(false)
  const [rescheduleReservationOpen, setRescheduleReservationOpen] = useState(false)
  const [patientDetailsOpen, setPatientDetailsOpen] = useState(false)
  const [showGuestEndCallModal, setShowGuestEndCallModal] = useState(false)
  const [startInterval, setStartInterval] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [isMouseOverControls, setIsMouseOverControls] = useState(false)
  const [hasParticipants, setHasParticipants] = useState(false)

  const patientData = {
    ...guests.find(u => u.profile_code === 'TVTVPACI'),
    full_name: meeting.guest.full_name,
    picture_url: meeting.guest.picture_url,
  }
  const isPsychologist = professional?.profile_code === 'TVTVPSIC'

  const createLocalTracks = useCallback(async () => {
    console.log('[JitsiRoom1] Criando tracks locais')

    try {
      const tracks = await JitsiMeet.createLocalTracks({
        devices: ['audio', 'video'],
        micDeviceId: activeMicrophoneDevice?.deviceId,
        cameraDeviceId: activeVideoDevice?.deviceId,
        resolution: isMobile ? 480 : 720,
        constraints
      })

      const audioTrack = tracks.find(t => t.getType() === 'audio')
      const videoTrack = tracks.find(t => t.getType() === 'video')

      console.log('[JitsiRoom1] Tracks criadas:', {
        audioId: audioTrack?.getId(),
        videoId: videoTrack?.getId()
      })

      return { audioTrack, videoTrack }
    } catch (error) {
      console.error('[JitsiRoom1] Erro ao criar tracks locais:', error)
      return { audioTrack: null, videoTrack: null }
    }
  }, [activeMicrophoneDevice, activeVideoDevice])

  const setupLocalTrackListeners = useCallback((track, trackType) => {
    track.on(JitsiMeet.events.track.LOCAL_TRACK_STOPPED, () => {
      console.log(`[JitsiRoom1] Track ${trackType} parada pelo usuário`)

      if (conferenceRef.current) {
        conferenceRef.current.removeTrack(track)
          .catch(err => console.error('[JitsiRoom1] Erro ao remover track:', err))
      }

      if (localTracksRef.current[trackType] === track) {
        localTracksRef.current[trackType] = null
      }

      track.dispose()

      if (trackType === 'video') {
        setVideoEnabled(false)
        setStatusMessage('Câmera foi desativada')
      } else if (trackType === 'audio') {
        setAudioEnabled(false)
        setStatusMessage('Microfone foi desativado')
      }
    })

    track.on(JitsiMeet.events.track.TRACK_MUTE_CHANGED, () => {
      const isMuted = track.isMuted()

      if (trackType === 'video') {
        setVideoEnabled(!isMuted)
      } else if (trackType === 'audio') {
        setAudioEnabled(!isMuted)
      }
    })

    track.on(JitsiMeet.events.track.NO_DATA_FROM_SOURCE, () => {
      console.warn(`[JitsiRoom1] ${trackType} não está produzindo dados`)

      const deviceName = trackType === 'video' ? 'câmera' : 'microfone'
      setStatusMessage(
        `Problema detectado: ${deviceName} não está funcionando. ` +
        'Verifique as permissões de mídia e se o dispositivo está conectado.'
      )

    })
  }, [])

  const replaceDevice = useCallback(async (deviceId, trackType) => {
    if (!conferenceRef.current) {
      console.warn('[JitsiRoom1] replaceDevice: conferência não iniciada')
      return
    }

    const currentTrack = localTracksRef.current[trackType]
    if (!currentTrack) {
      console.warn(`[JitsiRoom1] replaceDevice: sem ${trackType} track para substituir`)
      return
    }

    const queue = trackType === 'audio'
      ? replaceAudioTrackQueueRef.current
      : replaceVideoTrackQueueRef.current

    console.log(`[JitsiRoom1] Enfileirando troca de dispositivo ${trackType}:`, { deviceId })

    return new Promise((resolve, reject) => {
      queue.enqueue(async (onFinish) => {
        try {
          const track = localTracksRef.current[trackType]

          if (!track) {
            console.warn(`[JitsiRoom1] Track ${trackType} não existe mais`)
            resolve()
            onFinish()
            return
          }

          if (track.getDeviceId() === deviceId) {
            console.log(`[JitsiRoom1] Já está usando o dispositivo ${deviceId}`)
            resolve()
            onFinish()
            return
          }

          console.log(`[JitsiRoom1] Executando troca de ${trackType}:`, { deviceId })

          const isDefaultSelected = deviceId === 'default'
          let actualDeviceId = deviceId

          if (isDefaultSelected) {
            const kind = trackType === 'audio' ? 'audioinput' : 'videoinput'
            actualDeviceId = await getDefaultDeviceId(kind)

            if (trackType === 'audio') {
              isDefaultMicSelectedRef.current = true
            } else {
              isDefaultCameraSelectedRef.current = true
            }
          } else {
            if (trackType === 'audio') {
              isDefaultMicSelectedRef.current = false
            } else {
              isDefaultCameraSelectedRef.current = false
            }
          }

          const wasMuted = track.isMuted()

          if (trackType === 'video' && localVideoRef.current) {
            track.detach(localVideoRef.current)
          }

          const [newTrack] = await JitsiMeet.createLocalTracks({
            devices: [trackType],
            ...(trackType === 'audio'
              ? { micDeviceId: actualDeviceId }
              : { cameraDeviceId: actualDeviceId, resolution: isMobile ? 480 : 720 }
            ),
            constraints: {
              [trackType]: constraints[trackType]
            }
          })

          console.log(`[JitsiRoom1] Nova ${trackType} track criada:`, newTrack.getId())

          setupLocalTrackListeners(newTrack, trackType)
          await conferenceRef.current.replaceTrack(track, newTrack)

          if (isDefaultSelected && newTrack) {
            newTrack._realDeviceId = newTrack.deviceId
            newTrack.deviceId = 'default'
          }

          if (trackType === 'video' && localVideoRef.current) {
            newTrack.attach(localVideoRef.current)
          }

          if (wasMuted) {
            await newTrack.mute()
          }

          localTracksRef.current[trackType] = newTrack
          track.dispose()

          console.log(`[JitsiRoom1] Dispositivo ${trackType} trocado com sucesso`)
          resolve()
        } catch (error) {
          console.error(`[JitsiRoom1] Erro ao trocar dispositivo ${trackType}:`, error)
          reject(error)
        } finally {
          onFinish()
        }
      })
    })
  }, [setupLocalTrackListeners])

  useEffect(() => {
    if (!conferenceRef.current || !activeMicrophoneDevice) return

    const currentTrack = localTracksRef.current.audio
    if (!currentTrack) return

    const currentDeviceId = currentTrack.getDeviceId()
    if (currentDeviceId !== activeMicrophoneDevice.deviceId) {
      console.log('[JitsiRoom1] Dispositivo de áudio mudou, trocando...')
      replaceDevice(activeMicrophoneDevice.deviceId, 'audio')
    }
  }, [activeMicrophoneDevice, replaceDevice])

  useEffect(() => {
    if (!conferenceRef.current || !activeVideoDevice) return

    const currentTrack = localTracksRef.current.video
    if (!currentTrack) return

    const currentDeviceId = currentTrack.getDeviceId()
    if (currentDeviceId !== activeVideoDevice.deviceId) {
      console.log('[JitsiRoom1] Dispositivo de vídeo mudou, trocando...')
      replaceDevice(activeVideoDevice.deviceId, 'video')
    }
  }, [activeVideoDevice, replaceDevice])

  const handleToggleAudio = useCallback(async () => {
    const audioTrack = localTracksRef.current.audio
    if (!audioTrack) {
      console.warn('[JitsiRoom1] handleToggleAudio: sem audio track')
      return
    }

    try {
      if (audioEnabled) {
        await audioTrack.mute()
        console.log('[JitsiRoom1] Áudio mutado')
      } else {
        await audioTrack.unmute()
        console.log('[JitsiRoom1] Áudio desmutado')
      }
      setAudioEnabled(!audioEnabled)
    } catch (error) {
      console.error('[JitsiRoom1] Erro ao fazer toggle de áudio:', error)
    }
  }, [audioEnabled])

  const handleToggleVideo = useCallback(async () => {
    const videoTrack = localTracksRef.current.video
    if (!videoTrack) {
      console.warn('[JitsiRoom1] handleToggleVideo: sem video track')
      return
    }

    try {
      if (videoEnabled) {
        await videoTrack.mute()
        console.log('[JitsiRoom1] Vídeo mutado')
      } else {
        await videoTrack.unmute()
        console.log('[JitsiRoom1] Vídeo desmutado')
      }
      setVideoEnabled(!videoEnabled)
    } catch (error) {
      console.error('[JitsiRoom1] Erro ao fazer toggle de vídeo:', error)
    }
  }, [videoEnabled])

  const cleanupRemoteTracks = useCallback(async (participantId) => {
    const participantTracks = remoteTracksRef.current.get(participantId)
    if (participantTracks) {
      console.log('[JitsiRoom1] Limpando tracks remotas:', { participantId, tracksCount: participantTracks.length })

      for (const track of participantTracks) {
        try {
          track.dispose()
        } catch (error) {
          console.error('[JitsiRoom1] Erro ao descartar track remota:', error)
        }
      }
      remoteTracksRef.current.delete(participantId)
    }
  }, [])

  const cleanupJitsi = useCallback(async () => {
    console.log('[JitsiRoom1] Iniciando cleanup')

    for (const [participantId] of remoteTracksRef.current) {
      await cleanupRemoteTracks(participantId)
    }
    remoteTracksRef.current.clear()

    const { audio, video } = localTracksRef.current

    if (video && localVideoRef.current) {
      try {
        video.detach(localVideoRef.current)
      } catch (error) {
        console.error('[JitsiRoom1] Erro ao desanexar video:', error)
      }
    }

    if (conferenceRef.current) {
      try {
        const localTracks = conferenceRef.current.getLocalTracks()
        for (const track of localTracks) {
          try {
            await conferenceRef.current.removeTrack(track)
          } catch (e) {
            console.error('[JitsiRoom1] Erro ao remover track:', e)
          }
        }
      } catch (e) {
        console.error('[JitsiRoom1] Erro ao obter tracks locais:', e)
      }
    }

    if (audio) {
      try {
        audio.dispose()
      } catch (error) {
        console.error('[JitsiRoom1] Erro ao descartar audio:', error)
      }
    }
    if (video) {
      try {
        video.dispose()
      } catch (error) {
        console.error('[JitsiRoom1] Erro ao descartar video:', error)
      }
    }
    localTracksRef.current = { audio: null, video: null }

    if (conferenceRef.current) {
      try {
        await conferenceRef.current.leave()
      } catch (error) {
        console.error('[JitsiRoom1] Erro ao sair da conferência:', error)
      }
      conferenceRef.current = null
    }

    if (connectionRef.current) {
      try {
        await connectionRef.current.disconnect()
      } catch (error) {
        console.error('[JitsiRoom1] Erro ao desconectar:', error)
      }
      connectionRef.current = null
    }

    console.log('[JitsiRoom1] Cleanup concluído')
  }, [cleanupRemoteTracks])

  const handleExit = useCallback(() => {
    cleanupJitsi().then(() => {
      const { guest, schedule } = meeting
      const scheduleId = schedule?.id
      const friendlyUrl = guest?.friendly_url
      const professionalProfileCode = professional.profile_code
      window.location.assign(externalRoutes.SURVEY(scheduleId, friendlyUrl, professionalProfileCode))
    })
  }, [cleanupJitsi, meeting, professional])

  useEffect(() => {
    const JITSI_CONFIG = {
      name: meeting.schedule.hash,
      serverUrl: process.env.REACT_APP_JITSI_SERVER_URL,
      hosts: {
        domain: process.env.REACT_APP_JITSI_DOMAIN,
        muc: process.env.REACT_APP_JITSI_MUC,
        focus: `focus.${process.env.REACT_APP_JITSI_DOMAIN}`
      },
      serviceUrl: process.env.REACT_APP_JITSI_SERVICE_URL,
      websocketKeepAlive: true,
      useStunTurn: true,
    }

    const initializeJitsi = async () => {
      JitsiMeet.setLogLevel(JitsiMeet.logLevels.DEBUG)

      connectionRef.current = new JitsiMeet.JitsiConnection(null, null, JITSI_CONFIG)
      const connection = connectionRef.current

      connection.addEventListener(JitsiMeet.events.connection.CONNECTION_ESTABLISHED, onConnectionSuccess)
      connection.addEventListener(JitsiMeet.events.connection.CONNECTION_FAILED, onConnectionFailed)
      connection.addEventListener(JitsiMeet.events.connection.CONNECTION_DISCONNECTED, onConnectionDisconnected)

      await connection.connect()
    }

    const onConnectionSuccess = () => {
      console.log('[JitsiRoom1] Conexão estabelecida')

      if (conferenceRef.current) {
        console.log('[JitsiRoom1] Conferência já existe, ignorando reconexão')
        return
      }

      const conf = connectionRef.current.initJitsiConference(meeting.schedule.hash, {
        openBridgeChannel: true,
        p2p: { enabled: false },
        videoQuality: {
          codecPreferenceOrder: isMobile
            ? ['H264', 'VP8', 'VP9', 'AV1']
            : ['H264', 'VP9', 'VP8', 'AV1'],
          maxBitratesVideo: {
            H264: {
              low: 200000,      // 200 Kbps para 180p-240p
              standard: 700000, // 700 Kbps para 360p-480p
              high: 2000000,    // 2 Mbps para 720p
              fullHd: 3500000   // 3.5 Mbps para 1080p
            },
            VP8: {
              low: 200000,
              standard: 700000,
              high: 2000000,
              fullHd: 3500000
            },
            VP9: {
              low: 100000,
              standard: 500000,
              high: 1500000,
              fullHd: 2500000
            },
            AV1: {
              low: 100000,
              standard: 500000,
              high: 1500000,
              fullHd: 2500000
            }
          },
          minHeightForQualityLvl: {
            360: 'standard',
            720: 'high',
            1080: 'fullHd'
          },
          enableAdaptiveMode: true
        },
        enableLayerSuspension: true,
        enableAdaptiveSimulcast: true,
      })

      conferenceRef.current = conf
      setupConferenceListeners(conf)
      conf.setDisplayName(user.user_uuid)
      console.log('[JitsiRoom1] Entrando na conferência')
      conf.join()
    }

    const onConnectionFailed = (error) => {
      console.error('[JitsiRoom1] Falha na conexão:', error)
      history.replace('error')
    }

    const onConnectionDisconnected = () => {
      console.warn('[JitsiRoom1] Conexão desconectada')

      const wasInConference = conferenceRef.current !== null

      if (conferenceRef.current) {
        conferenceRef.current = null
      }
      if (connectionRef.current) {
        connectionRef.current = null
      }

      if (wasInConference) {
        setStatusMessage('Servidor indisponível. Encerrando chamada...')
        setTimeout(() => {
          history.replace({
            pathname: 'error',
            state: { errorType: errorTypes.UNEXPECTED }
          })
        }, 3000)
      } else {
        setStatusMessage('Desconectado. Verifique sua conexão.')
        setTimeout(() => {
          history.replace({
            pathname: 'error',
            state: { errorType: errorTypes.UNEXPECTED }
          })
        }, 3000)
      }
    }

    const setupConferenceListeners = (conf) => {
      conf.on(JitsiMeet.events.conference.CONFERENCE_JOINED, onConferenceJoined)
      conf.on(JitsiMeet.events.conference.CONFERENCE_FAILED, onConferenceFailed)
      conf.on(JitsiMeet.events.conference.USER_JOINED, onUserJoined)
      conf.on(JitsiMeet.events.conference.USER_LEFT, onUserLeft)
      conf.on(JitsiMeet.events.conference.TRACK_ADDED, onTrackAdded)
      conf.on(JitsiMeet.events.conference.TRACK_REMOVED, onTrackRemoved)
      conf.on(JitsiMeet.events.conference.TRACK_MUTE_CHANGED, onTrackMuteChanged)
      conf.on(JitsiMeet.events.conference.MESSAGE_RECEIVED, onMessageReceived)
      conf.on(JitsiMeet.events.connectionQuality.LOCAL_STATS_UPDATED, onLocalNetworkQualityLevelChanged)
      conf.on(JitsiMeet.events.connectionQuality.REMOTE_STATS_UPDATED, onParticipantNetworkQualityLevelChanged)
      conf.on(JitsiMeet.events.conference.KICKED, onKicked)
    }

    initializeJitsi()

    return () => {
      if (connectionRef.current) {
        connectionRef.current.removeEventListener(JitsiMeet.events.connection.CONNECTION_ESTABLISHED, onConnectionSuccess)
        connectionRef.current.removeEventListener(JitsiMeet.events.connection.CONNECTION_FAILED, onConnectionFailed)
        connectionRef.current.removeEventListener(JitsiMeet.events.connection.CONNECTION_DISCONNECTED, onConnectionDisconnected)
      }
      clearTimeout(hideControlsTimerRef.current)
      cleanupJitsi()
    }
  }, [meeting.schedule.hash, cleanupJitsi, history, user.user_uuid])

  const isTranscriptionAllowed = meeting.patientPreferences?.transcribe_audio

  const onConferenceJoined = useCallback(async () => {
    console.log('[JitsiRoom1] Conferência joined')

    const { audioTrack, videoTrack } = await createLocalTracks()

    if (audioTrack) {
      try {
        setupLocalTrackListeners(audioTrack, 'audio')
        await conferenceRef.current.addTrack(audioTrack)
        localTracksRef.current.audio = audioTrack
      } catch (error) {
        console.error('[JitsiRoom1] Erro ao adicionar audio track:', error)
      }
    }

    if (videoTrack) {
      try {
        setupLocalTrackListeners(videoTrack, 'video')
        await conferenceRef.current.addTrack(videoTrack)
        localTracksRef.current.video = videoTrack

        if (localVideoRef.current) {
          videoTrack.attach(localVideoRef.current)
        }
      } catch (error) {
        console.error('[JitsiRoom1] Erro ao adicionar video track:', error)
      }
    }

    try {
      const preferredHeight = isMobile ? 720 : 1080

      if (conferenceRef.current.setSenderVideoConstraint) {
        conferenceRef.current.setSenderVideoConstraint(preferredHeight)
      }

      if (conferenceRef.current.setReceiverConstraints) {
        conferenceRef.current.setReceiverConstraints({
          lastN: -1,
          defaultConstraints: { maxHeight: preferredHeight },
        })
      }
    } catch (error) {
      console.error('[JitsiRoom1] Erro ao configurar qualidade de vídeo:', error)
    }

    if (isTranscriptionAllowed) {
      try {
        conferenceRef.current.setLocalParticipantProperty('requestingTranscription', 'true')
        conferenceRef.current.setLocalParticipantProperty('transcription_language', 'pt-BR')
      } catch (error) {
        console.error('[JitsiRoom1] Erro ao configurar transcrição:', error)
      }
    }

  }, [createLocalTracks, setupLocalTrackListeners, isTranscriptionAllowed])

  const onConferenceFailed = useCallback(async (errorCode, ...args) => {
    const errorName = errorCode?.name || errorCode
    console.error('[JitsiRoom1] Conferência falhou:', errorName, args)

    if (RELOAD_ERRORS.includes(errorName)) {
      setStatusMessage('Servidor atualizado. Reconectando...')
      setTimeout(() => window.location.reload(), 2000)
      return
    }

    if (LEAVE_BEFORE_DISCONNECT_ERRORS.includes(errorName)) {
      const errorMessage = ERROR_MESSAGES[errorName] || 'Erro na conexão. Encerrando chamada.'
      setStatusMessage(errorMessage)

      try {
        conferenceRef.current = null
        if (connectionRef.current) {
          await connectionRef.current.disconnect()
        }
      } catch (err) {
        console.error('[JitsiRoom1] Erro ao sair graciosamente:', err)
      }

      setTimeout(() => history.replace('error'), 3000)
      return
    }

    if (NOTIFY_ONLY_ERRORS.includes(errorName)) {
      const errorMessage = ERROR_MESSAGES[errorName] || 'Problema temporário de conexão'
      setStatusMessage(errorMessage)
      return
    }

    switch (errorName) {
    case 'conference.connectionError': {
      setStatusMessage('Problema de conexão. Reconectando...')
      return
    }

    case 'conference.setupFailed': {
      setStatusMessage('Falha ao configurar conferência.')
      setTimeout(() => history.replace('error'), 3000)
      return
    }

    case 'conference.authenticationRequired': {
      setStatusMessage('Esta sala requer autenticação.')
      setTimeout(() => history.replace('error'), 3000)
      return
    }

    case 'connection.connectionDropped':
    case 'connection.serverError': {
      const errorMessage = ERROR_MESSAGES[errorName] || 'Erro de conexão'
      setStatusMessage(errorMessage)
      return
    }

    case 'connection.connectionFailed': {
      setStatusMessage('Falha ao conectar. Verifique sua internet.')
      setTimeout(() => history.replace('error'), 3000)
      return
    }

    case 'connection.otherError': {
      setStatusMessage('Erro de conexão. Tentando reconectar...')
      return
    }

    case 'conference.maxUsersLimitReached': {
      setStatusMessage('Sala lotada. Número máximo de participantes atingido.')
      setTimeout(() => history.replace('error'), 3000)
      return
    }

    case 'conference.conferenceAlreadyJoined': {
      console.warn('[JitsiRoom1] Tentativa de entrar em conferência já ativa')
      return
    }

    case 'conference.conferenceDestroyed': {
      setStatusMessage('Conferência encerrada.')
      setTimeout(() => history.replace('error'), 2000)
      return
    }

    case 'conference.chatError': {
      console.error('[JitsiRoom1] Erro no chat:', args)
      return
    }

    case 'conference.bridgeDown': {
      setStatusMessage('Servidor de mídia indisponível. Reconectando...')
      return
    }

    case 'conference.resourceConstraint': {
      setStatusMessage('Recursos insuficientes. Tente novamente.')
      setTimeout(() => history.replace('error'), 3000)
      return
    }

    case 'conference.gracefulShutdown': {
      console.log('[JitsiRoom1] Servidor em manutenção')
      setStatusMessage('Servidor em manutenção. Tente novamente mais tarde.')
      setTimeout(() => history.replace('error'), 3000)
      return
    }

    default:
      // eslint-disable-next-line no-case-declarations
      const errorMessage = ERROR_MESSAGES[errorName] || 'Erro desconhecido na conferência'
      setStatusMessage(errorMessage)
      setTimeout(() => history.replace('error'), 3000)
    }
  }, [history])

  const onUserJoined = useCallback((id, participant) => {
    const displayName = participant?.getDisplayName?.() || ''
    console.log('[JitsiRoom1] Participante entrou na sala:', {
      participantId: id,
      displayName
    })

    // Cacheia o displayName para uso no onUserLeft
    participantNamesRef.current.set(id, displayName)

    // Ignora transcriber
    if (displayName.toLowerCase() === 'transcriber') {
      console.log('[JitsiRoom1] Transcriber entrou, ignorando na UI')
      transcriberIdsRef.current.add(id)
      return
    }

    // Ignora entrada da própria sessão duplicada
    if (displayName === user.user_uuid) {
      console.log('[JitsiRoom1] Própria sessão duplicada entrou, ignorando')
      return
    }

    setStatusMessage('')
    setParticipantLeft(false)
    setHasParticipants(true)
  }, [user.user_uuid])

  const onUserLeft = useCallback((id) => {
    console.log('[JitsiRoom1] Participante saiu:', id)

    // Usa cache pois getParticipantById pode retornar null após kick
    const displayName = participantNamesRef.current.get(id)
    participantNamesRef.current.delete(id)

    // Ignora transcriber
    if (transcriberIdsRef.current.has(id)) {
      console.log('[JitsiRoom1] Transcriber saiu, ignorando na UI')
      transcriberIdsRef.current.delete(id)
      cleanupRemoteTracks(id)
      return
    }

    // Ignora saída da própria sessão antiga (kick por duplicata)
    if (displayName === user.user_uuid) {
      console.log('[JitsiRoom1] Sessão antiga do próprio usuário saiu, ignorando')
      cleanupRemoteTracks(id)
      return
    }

    // Verifica se é sessão duplicada de outro usuário sendo removida
    // (o mesmo usuário ainda está presente com outra sessão)
    if (displayName && conferenceRef.current) {
      const participants = conferenceRef.current.getParticipants()
      const sameUserStillPresent = participants.some(p =>
        p.getDisplayName() === displayName && p.getId() !== id
      )
      if (sameUserStillPresent) {
        console.log('[JitsiRoom1] Sessão duplicada de outro usuário saiu, mas ele ainda está presente. Ignorando.')
        cleanupRemoteTracks(id)
        return
      }
    }

    const participantName = meeting.guest.first_name || 'Participante'
    setStatusMessage(`${participantName} saiu da sala.`)
    setParticipantLeft(true)
    setParticipantNetwork(null)
    setHasParticipants(false)
    setIsRemoteAudioMuted(false)
    setIsRemoteVideoMuted(false)
    cleanupRemoteTracks(id)
  }, [meeting.guest.first_name, cleanupRemoteTracks, user.user_uuid])

  const onKicked = useCallback((participant, reason) => {
    console.log('[JitsiRoom1] Kicked da conferência:', { participant, reason })
    history.replace({
      pathname: 'error',
      state: { errorType: errorTypes.ALREADY_CONNECTED }
    })

  }, [history])

  const onTrackAdded = useCallback((track) => {
    if (track.isLocal()) return

    const participantId = track.getParticipantId()
    const type = track.getType()

    console.log('[JitsiRoom1] Track remota adicionada:', { participantId, type, trackId: track.getId() })

    if (transcriberIdsRef.current.has(participantId)) {
      console.log('[JitsiRoom1] Track do transcriber, ignorando na UI')
      return
    }

    if (!remoteTracksRef.current.has(participantId)) {
      remoteTracksRef.current.set(participantId, [])
    }
    remoteTracksRef.current.get(participantId).push(track)

    try {
      if (type === 'video' && remoteVideoRef.current) {
        track.attach(remoteVideoRef.current)
        setIsRemoteVideoMuted(track.isMuted())
      } else if (type === 'audio' && remoteAudioRef.current) {
        track.attach(remoteAudioRef.current)
        setIsRemoteAudioMuted(track.isMuted())
      }
    } catch (error) {
      console.error(`[JitsiRoom1] Erro ao anexar track ${type}:`, error)
    }
  }, [])

  const onTrackRemoved = useCallback((track) => {
    if (track.isLocal()) return

    const participantId = track.getParticipantId()
    console.log('[JitsiRoom1] Track remota removida:', { participantId, type: track.getType() })

    const participantTracks = remoteTracksRef.current.get(participantId)
    if (participantTracks) {
      const index = participantTracks.indexOf(track)
      if (index > -1) {
        participantTracks.splice(index, 1)
        if (participantTracks.length === 0) {
          remoteTracksRef.current.delete(participantId)
        }
      }
    }

    track.dispose()
  }, [])

  const updateFeedbackMessage = useCallback((audioMuted, videoMuted) => {
    if (audioMuted && videoMuted) {
      setStatusMessage('O participante desligou a câmera e o microfone')
    } else if (videoMuted) {
      setStatusMessage('O participante desligou a câmera')
    } else if (audioMuted) {
      setStatusMessage('O participante desligou o microfone')
    } else {
      setStatusMessage('')
    }
  }, [])

  useEffect(() => {
    if (hasParticipants && !participantLeft) {
      updateFeedbackMessage(isRemoteAudioMuted, isRemoteVideoMuted)
    }
  }, [hasParticipants, isRemoteAudioMuted, isRemoteVideoMuted, participantLeft, updateFeedbackMessage])

  const onTrackMuteChanged = useCallback((track) => {
    if (track.isLocal()) return

    const participantId = track.getParticipantId()
    if (transcriberIdsRef.current.has(participantId)) return

    const isMuted = track.isMuted()
    const trackType = track.getType()

    console.log('[JitsiRoom1] Track remota mudou mute:', { type: trackType, isMuted })

    if (trackType === 'audio') {
      setIsRemoteAudioMuted(isMuted)
    } else if (trackType === 'video') {
      setIsRemoteVideoMuted(isMuted)
    }
  }, [])

  const onMessageReceived = useCallback((id, text) => {
    const participant = conferenceRef.current?.getParticipantById(id)
    const displayName = participant?.getDisplayName()
    const author = displayName === meeting.guest.user_uuid
      ? (meeting.guest.first_name || 'participante')
      : (meeting.user.first_name || 'você')

    setChatMessage({
      participantId: id,
      author: author,
      text: text,
      timestamp: new Date(),
    })
  }, [meeting.guest.user_uuid, meeting.guest.first_name, meeting.user.first_name])

  const onLocalNetworkQualityLevelChanged = useCallback((stats) => {
    const qualityScore = getQualityScoreFromPercentage(stats.connectionQuality)
    setLocalNetwork(qualityScore)
  }, [])

  const onParticipantNetworkQualityLevelChanged = useCallback((_id, stats) => {
    if (transcriberIdsRef.current.has(_id)) return

    const qualityScore = getQualityScoreFromPercentage(stats.connectionQuality)
    setParticipantNetwork(qualityScore)
  }, [])

  useEffect(() => {
    if (audioEnabled !== undefined) {
      fetchApi.sendLogToServer(meeting.schedule.hash, {
        event: audioEnabled ? 'GUEST_ENABLED_AUDIO' : 'GUEST_DISABLED_AUDIO'
      })
    }
  }, [audioEnabled, meeting.schedule.hash])

  useEffect(() => {
    if (videoEnabled !== undefined) {
      fetchApi.sendLogToServer(meeting.schedule.hash, {
        event: videoEnabled ? 'GUEST_ENABLED_VIDEO' : 'GUEST_DISABLED_VIDEO'
      })
    }
  }, [videoEnabled, meeting.schedule.hash])

  const onSendMessage = useCallback((text) => {
    if (conferenceRef.current && text.trim()) {
      conferenceRef.current.sendMessage(text)
    }
  }, [])

  const handleLeaveCall = useCallback(() => {
    setShowGuestEndCallModal(state => !state)
  }, [])

  const handleStartInterval = useCallback(() => {
    setShowControls(true)
    if (meeting.schedule.interval === 0) handleExit()
    else setStartInterval(true)
  }, [meeting.schedule.interval, handleExit])

  const handleMouseMove = useCallback(() => {
    clearTimeout(hideControlsTimerRef.current)
    setShowControls(true)

    if (!isMouseOverControls) {
      hideControlsTimerRef.current = setTimeout(() => {
        setShowControls(false)
      }, 3000)
    }
  }, [isMouseOverControls])

  const handleDisplayMobile = throttle(() => setShowControls(true), 1500, { leading: true, trailing: false })

  const handleOpenSettings = useCallback(() => {
    onOpenSettingsModal()
  }, [onOpenSettingsModal])

  const handleReportError = useCallback((reportData) => {
    const issue = {
      schedule_id: meeting.schedule.id,
      user_uuid: meeting.user.user_uuid,
      message: reportData.message,
      reason: reportData.reason,
      video_provider: meeting?.schedule?.videoProvider
    }

    fetchNocoDB.createIssue(issue)
  }, [meeting])

  return (
    <div className='CallRoom' onMouseMove={handleMouseMove} onTouchStart={handleDisplayMobile}>
      {showGuestEndCallModal && <GuestEndCallModal handleClick={handleLeaveCall} handleExit={handleExit} />}

      <PatientDetails open={patientDetailsOpen} setOpen={setPatientDetailsOpen} patientData={patientData} />

      <Reservation
        open={reservationOpen}
        setOpen={setReservationOpen}
        professional={meeting.user}
        patient={meeting.guest}
        patientPlanCode={meeting.schedule.planCode}
        refetchReservation={refetchReservation}
        isPsychologist={isPsychologist}
      />

      {reservation && (
        <RescheduleReservation
          reservation={reservation}
          onRefresh={refetchReservation}
          handleClose={() => setRescheduleReservationOpen(false)}
          open={rescheduleReservationOpen}
        />
      )}

      <ChatContainer
        isChatOpen={isChatOpen}
        chatMessage={chatMessage}
        handleToggleChat={() => {
          setIsChatOpen(prev => !prev)
          fetchApi.sendLogToServer(meeting.schedule.hash, {
            event: 'GUEST_ENABLED_CHAT',
          })
        }}
        handleSendMessage={onSendMessage}
      />

      <div className='CallRoom__components'>
        <div
          className='CallRoom__components__header'
          onMouseEnter={() => setIsMouseOverControls(true)}
          onMouseLeave={() => setIsMouseOverControls(false)}
        >
          <div className='CallRoom__components__header--left'>
            <CallHeader startInterval={handleStartInterval} showElement={showControls} meeting={meeting} />
            {startInterval && <CallHeaderAlert onExit={handleExit} interval={meeting.schedule?.interval ?? 10} />}
          </div>

          <div className='CallRoom__components__header--right'>
            {isRemoteAudioMuted && showControls && (
              <div className='Participant__mic-off'>
                <CustomIcons icon={iconList.MicrophoneOff} fill={colors.WHITE} width='14.97px' height='15.8px' />
              </div>
            )}

            <NetworkSignal networkSignal={participantNetwork} />
          </div>
        </div>

        <div className='CallRoom__components__main'>
          {statusMessage && <FeedbackCallMessageBox>{statusMessage}</FeedbackCallMessageBox>}

          <div className='LocalVideoContainer'>
            {!videoEnabled && (
              <div className='LocalVideoContainer__icon'>
                <CustomIcons icon={iconList.CameraOff} fill={colors.WHITE} />
              </div>
            )}

            <NetworkSignal networkSignal={localNetwork} />
            <video ref={localVideoRef} autoPlay playsInline muted className='__openreplay-block' />
          </div>
        </div>

        <div
          className='CallRoom__components__footer'
          onMouseEnter={() => setIsMouseOverControls(true)}
          onMouseLeave={() => setIsMouseOverControls(false)}
        >
          <CallControllers
            showElement={showControls}
            toggleChatBox={() => {
              setIsChatOpen(prev => !prev)
              fetchApi.sendLogToServer(meeting.schedule.hash, {
                event: 'GUEST_DISABLED_CHAT',
              })
            }}
            toggleModal={handleLeaveCall}
            isProfessional={professional?.username === meeting.user.username}
            onSettingsClick={handleOpenSettings}
            onReservationClick={() => setReservationOpen(b => !b)}
            onRescheduleReservationClick={() => setRescheduleReservationOpen(prevState => !prevState)}
            isAudioEnabled={audioEnabled}
            isVideoEnabled={videoEnabled}
            onToggleAudio={handleToggleAudio}
            onToggleVideo={handleToggleVideo}
            showReservationOption={isPsychologist}
            showRescheduleReservationOption={isPsychologist && !!reservation}
            onPatientDetailsClick={() => setPatientDetailsOpen(oldState => !oldState)}
          />
        </div>
      </div>

      <div className={`MainVideoContainer ${isRemoteVideoMuted ? 'videoOff' : 'videoOn'}`}>
        {!participantLeft && (
          <video ref={remoteVideoRef} playsInline autoPlay className='__openreplay-block' />
        )}
        <audio ref={remoteAudioRef} autoPlay className='__openreplay-block' />
      </div>

      <ReportErrorButton onSubmit={handleReportError} showControls={showControls} />
    </div>
  )
}

export default JitsiRoom